import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';

import { ThinProfileSort } from '@/enums/ThinProfileSort';
import { apiClient, ICollectionResponse } from '@/resources/upgrade/jsonapi-client';
import { enrichThinProfile, IThinProfile } from '@/resources/upgrade/ThinProfile';

export interface IKeyword {
  id: string;
  type: 'keywords';
  name: string;
}

export function landingThinProfilesQueryOptions() {
  return {
    queryKey: ['thin-profiles', 'landing'],
    queryFn: async () => {
      const response = await apiClient.get<ICollectionResponse<IThinProfile>>('thin_profiles', {
        'filter[library_featured]': 'true',
        'page[size]': String(5),
        'page[number]': String(1),
      });

      return response.data.map(enrichThinProfile);
    },
    staleTime: 1000 * 60 * 5,
  };
}

export function thinProfileQueryOptions(id: string | undefined) {
  return {
    queryKey: ['thin-profiles', id],
    queryFn: async () => {
      const response = await apiClient.get<IThinProfile>(`thin_profiles/${id}`);

      return enrichThinProfile(response);
    },
    enabled: Boolean(id),
    staleTime: 1000 * 60 * 5,
  };
}

export function profileKeywordsQueryOptions(id: string | undefined) {
  return {
    queryKey: ['thin-profiles', id, 'keywords'],
    queryFn: async () => {
      const response = await apiClient.get<ICollectionResponse<IKeyword>>(`thin_profiles/${id}/keywords`, {
        'page[size]': '15',
        'page[number]': '1',
      });

      return response.data;
    },
    enabled: Boolean(id),
    staleTime: 1000 * 60 * 5,
  };
}

export function blockedUserQueryOptions(myProfileId: string | undefined, userId: string | undefined) {
  return {
    queryKey: ['thin-profiles', myProfileId, 'blocked', userId],
    queryFn: async () => {
      const response = await apiClient.get<ICollectionResponse<IThinProfile>>(
        `thin_profiles/${myProfileId}/blocked_profiles`,
        {
          'filter[user_id]': userId!,
          'page[size]': '15',
          'page[number]': '1',
        }
      );

      return response.data.map(enrichThinProfile);
    },
    enabled: Boolean(myProfileId) && Boolean(userId),
  };
}

export function allBlockedUsersQueryOptions(myProfileId: string | undefined) {
  return {
    queryKey: ['thin-profiles', myProfileId, 'blocked'],
    queryFn: async () => {
      const response = await apiClient.get<ICollectionResponse<IThinProfile>>(
        `thin_profiles/${myProfileId}/blocked_profiles`,
        {
          'page[size]': '15',
          'page[number]': '1',
        }
      );

      return response.data.map(enrichThinProfile);
    },
    enabled: Boolean(myProfileId),
  };
}

export function thinProfilesForIndexSearchQueryOptions(
  filters: Record<string, string | Array<string> | undefined>,
  pageSize: number,
  pageNumber: number
) {
  const cleanFilters = omitBy(filters, isNil) as Record<string, string | Array<string>>;

  return {
    queryKey: ['thin-profiles', 'index-search', cleanFilters, pageSize, pageNumber],
    queryFn: async () => {
      const params: Record<string, string> = {
        sort: 'algolia_order',
        'page[size]': pageSize.toString(),
        'page[number]': pageNumber.toString(),
      };

      // Add filters
      for (const [key, value] of Object.entries(cleanFilters)) {
        if (Array.isArray(value)) {
          params[`filter[${key}]`] = value.join(',');
        } else {
          params[`filter[${key}]`] = value;
        }
      }

      const response = await apiClient.get<ICollectionResponse<IThinProfile>>('thin_profiles', params);

      return {
        data: response.data.map(enrichThinProfile),
        meta: response.meta,
      };
    },
    staleTime: 1000 * 60 * 5,
  };
}

export function lectureCommentProfilesQueryOptions(userId: string | Array<string> | undefined) {
  return {
    queryKey: ['thin-profiles', 'comment-authors', userId],
    queryFn: async () => {
      const filterValue = Array.isArray(userId) ? userId.join(',') : userId;
      const response = await apiClient.get<ICollectionResponse<IThinProfile>>('thin_profiles', {
        'filter[user_id]': filterValue!,
      });

      return response.data.map(enrichThinProfile);
    },
    enabled: Boolean(userId),
  };
}

// People autocomplete for @-mentions in community posts
export function mentionProfilesQueryOptions(eventId: string | undefined, query: string) {
  return {
    queryKey: ['thin-profiles', 'mentions', eventId, query],
    queryFn: async () => {
      const response = await apiClient.get<ICollectionResponse<IThinProfile>>('thin_profiles', {
        'filter[with_ticket_for_event]': eventId!,
        'filter[query]': query,
        sort: 'last_name,first_name',
        'page[size]': '8',
        'page[number]': '1',
      });

      return response.data.map(enrichThinProfile);
    },
    enabled: Boolean(eventId) && query.length >= 1,
    staleTime: 1000 * 60 * 5,
    // keep showing the previous list while the next keystroke's results load
    placeholderData: (previousData: Array<IThinProfile> | undefined) => previousData,
  };
}

// Infinite query options for speakers lists
export function thinProfileInfiniteQueryOptions(
  filters: Record<string, string | Array<string> | undefined>,
  pageSize = 16,
  sort: ThinProfileSort | string = ThinProfileSort.LastName
) {
  const cleanFilters = omitBy(filters, isNil) as Record<string, string | Array<string>>;

  return {
    queryKey: ['thin-profiles', 'infinite', cleanFilters, pageSize, sort],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const params: Record<string, string> = {
        sort: `avatar_existence,${sort},first_name,-created_at`,
        include: 'user',
        'page[size]': pageSize.toString(),
        'page[number]': pageParam.toString(),
      };

      for (const [key, value] of Object.entries(cleanFilters)) {
        if (Array.isArray(value)) {
          params[`filter[${key}]`] = value.join(',');
        } else {
          params[`filter[${key}]`] = value;
        }
      }

      const response = await apiClient.get<ICollectionResponse<IThinProfile>>('thin_profiles', params);

      return {
        data: response.data.map(enrichThinProfile),
        meta: response.meta,
        links: response.links,
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => (lastPage.links?.next ? allPages.length + 1 : undefined),
  };
}

export function thinProfileInfiniteNoSortQueryOptions(
  filters: Record<string, string | Array<string> | undefined>,
  sort: Array<string> | null,
  pageSize = 16
) {
  const cleanFilters = omitBy(filters, isNil) as Record<string, string | Array<string>>;

  return {
    queryKey: ['thin-profiles', 'infinite-nosort', cleanFilters, sort, pageSize],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const params: Record<string, string> = {
        include: 'user',
        'page[size]': pageSize.toString(),
        'page[number]': pageParam.toString(),
      };

      if (sort) {
        params['sort'] = sort.join(',');
      }

      for (const [key, value] of Object.entries(cleanFilters)) {
        if (Array.isArray(value)) {
          params[`filter[${key}]`] = value.join(',');
        } else {
          params[`filter[${key}]`] = value;
        }
      }

      const response = await apiClient.get<ICollectionResponse<IThinProfile>>('thin_profiles', params);

      return {
        data: response.data.map(enrichThinProfile),
        meta: response.meta,
        links: response.links,
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => (lastPage.links?.next ? allPages.length + 1 : undefined),
  };
}
